if (!customElements.get("od-product-form")) {
  /**
   * @class ProductForm
   * @extends HTMLElement
   */

  class ProductForm extends HTMLElement {
    selectors = {
      cartDrawer: "cart-drawer",
      idInput: "[name='id']",
      submit: "[data-add-to-cart]",
      submitText: "[data-add-to-cart-text]",
      message: "[data-product-form-message]",
      backInStock: "[data-klaviyo-back-in-stock-trigger]"
    };

    modifiers = {
      hidden: "hidden",
      loading: "loading"
    };

    messageTypes = {
      success: "success",
      error: "error"
    };

    constructor() {
      super();

      this._onVariantChanged = this._onVariantChange.bind(this);
      this._onFormSubmitted = this._onFormSubmit.bind(this);
      this._onCartErrorReceived = this._onCartError.bind(this);
    }

    get _isProductAvailable() {
      return this.getAttribute("is-product-available") === "true";
    }

    get _enableBackInStock() {
      return this.getAttribute("enable-back-in-stock") === "true";
    }

    get _isDetached() {
      return this.hasAttribute("detached");
    }

    connectedCallback() {
      this.productForm = document.forms[this.getAttribute("form")];

      this.idInputElement = this.querySelector(this.selectors.idInput);

      this.submitButtonElement = this.querySelector(this.selectors.submit);
      this.submitButtonTextElement = this.querySelector(this.selectors.submitText);

      this.backInStockButtonElement = this.querySelector(this.selectors.backInStock);

      this.messageElement = this.parentElement.querySelector(this.selectors.message);

      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      if (!this._isDetached) {
        this.productForm?.addEventListener("submit", this._onFormSubmitted);
      }

      this.productForm?.addEventListener("variant:change", this._onVariantChanged);

      this._subscribeToCartError();
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener("submit", this._onFormSubmitted);
      this.productForm?.removeEventListener("variant:change", this._onVariantChanged);

      this._unsubscribeFromCartError();
    }

    _onVariantChange(event) {
      const variant = event.detail.variant;

      if (variant && this.idInputElement) {
        this.idInputElement.value = variant.id;
      }

      if (!this.submitButtonElement || !this.submitButtonTextElement) {
        return;
      }

      if (!variant) {
        this.submitButtonElement.disabled = true;
        this.submitButtonTextElement.innerText = theme.strings.product.unavailable;

        if (this._enableBackInStock) {
          this.submitButtonElement.classList.remove(this.modifiers.hidden);
          this.backInStockButtonElement.classList.add(this.modifiers.hidden);
        }

        return;
      }

      if (!this._isProductAvailable) {
        this.submitButtonElement.disabled = true;
        this.submitButtonTextElement.innerText = theme.strings.product.sold_out;

        if (this._enableBackInStock) {
          this.submitButtonElement.classList.remove(this.modifiers.hidden);
          this.backInStockButtonElement.classList.add(this.modifiers.hidden);
        }
      } else {
        if (variant.available) {
          this.submitButtonElement.disabled = false;
          this.submitButtonTextElement.innerText = theme.strings.product.add_to_cart;

          if (this._enableBackInStock) {
            this.submitButtonElement.classList.remove(this.modifiers.hidden);
            this.backInStockButtonElement.classList.add(this.modifiers.hidden);
          }
        } else {
          this.submitButtonElement.disabled = true;
          this.submitButtonTextElement.innerText = theme.strings.product.sold_out;

          if (this._enableBackInStock) {
            this.submitButtonElement.classList.add(this.modifiers.hidden);
            this.backInStockButtonElement.classList.remove(this.modifiers.hidden);
          }
        }
      }
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this.productForm);

      this._updateFormMessage();

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      this.classList.add(this.modifiers.loading);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });

        this._onFormSubmitError(responseJson.description);
      }

      this.classList.remove(this.modifiers.loading);
    }

    _onFormSubmitSuccess(response) {
      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }

    _onFormSubmitError(message) {
      this._updateFormMessage(this.messageTypes.error, message);

      if (this._isDetached) {
        this.productForm?.scrollIntoView({
          behavior: "smooth"
        });
      }
    }

    _updateFormMessage(type, message) {
      if (!this.messageElement) {
        return;
      }

      switch (type) {
        case this.messageTypes.error: {
          this.messageElement.innerHTML = message;
          this.messageElement.classList.remove(this.modifiers.hidden);

          break;
        }

        default: {
          this.messageElement.innerHTML = "";
          this.messageElement.classList.add(this.modifiers.hidden);
        }
      }

      this.messageElement.classList.toggle(this.modifiers.hidden, !!!message);
    }

    _subscribeToCartError() {
      this._unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, this._onCartErrorReceived);
    }

    _unsubscribeFromCartError() {
      if (this._unsubscribeCartError) this._unsubscribeCartError();
    }

    _onCartError(event) {
      if (!this._isDetached) {
        return;
      }

      if (event.source !== "product-form") {
        return;
      }

      this._onFormSubmitError(event.message);
    }
  }

  customElements.define("od-product-form", ProductForm);
}

if (!customElements.get("product-price")) {
  /**
   * @class ProductPrice
   * @extends HTMLElement
   */

  class ProductPrice extends HTMLElement {
    selectors = {
      regularPrice: "[data-product-price]",
      comparePrice: "[data-product-compare-price]"
    };

    modifiers = {
      hidden: "hidden"
    };

    constructor() {
      super();

      this._onVariantChanged = this._onVariantChange.bind(this);
    }

    get _currencyFormat() {
      return this.getAttribute("currency-format") || theme.settings.currencyFormat;
    }

    get _trimTrailingZeroes() {
      return this.getAttribute("trim-trailing-zeroes") === "true";
    }

    connectedCallback() {
      this.productForm = document.forms[this.getAttribute("form")];

      this.priceElement = this.querySelector(this.selectors.regularPrice);
      this.comparePriceElement = this.querySelector(this.selectors.comparePrice);

      this.productForm?.addEventListener("variant:change", this._onVariantChanged);
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener("variant:change", this._onVariantChanged);
    }

    _onVariantChange(event) {
      const variant = event.detail.variant;

      this.classList.toggle(this.modifiers.hidden, !variant);

      if (!variant) {
        return;
      }

      this.priceElement.innerText = theme.formatMoney(variant.price, this._currencyFormat, this._trimTrailingZeroes);

      if (this.comparePriceElement) {
        if (variant.compare_at_price > variant.price) {
          this.comparePriceElement.innerText = theme.formatMoney(variant.compare_at_price, this._currencyFormat, this._trimTrailingZeroes);
          this.comparePriceElement.classList.remove(this.modifiers.hidden);
        } else {
          this.comparePriceElement.innerText = "";
          this.comparePriceElement.classList.add(this.modifiers.hidden);
        }
      }
    }
  }

  customElements.define("product-price", ProductPrice);
}
